import { Lock, Sparkles } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { LIMITS } from '@shared/constants/limits';
import { validateMaxParticipants, validateNickname, validatePassword, validateRoomName } from '@shared/schemas/validation';
import { Stepper } from '@/components/Stepper';
import { TextField } from '@/components/TextField';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { ApiRequestError, api } from '@/services/api';
import { saveSession } from '@/services/session';
import { toast } from '@/stores/toastStore';

type Errors = Partial<Record<'name' | 'maxParticipants' | 'nickname' | 'password' | 'form', string>>;

export function CreateRoomPage() {
  useDocumentMeta({
    title: '방 만들기',
    description: '회원가입 없이 점심 메뉴 방을 만들고 동료를 초대하세요. 인원과 닉네임만 정하면 10초면 끝나요.',
    path: '/create',
  });
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [maxParticipants, setMaxParticipants] = useState<number>(LIMITS.DEFAULT_PARTICIPANTS);
  const [nickname, setNickname] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const next: Errors = {};
    const vName = validateRoomName(name);
    const vMax = validateMaxParticipants(maxParticipants);
    const vNick = validateNickname(nickname);
    const vPw = validatePassword(usePassword ? password : '');
    if (!vName.ok) next.name = vName.message;
    if (!vMax.ok) next.maxParticipants = vMax.message;
    if (!vNick.ok) next.nickname = vNick.message;
    if (!vPw.ok) next.password = vPw.message;
    if (usePassword && vPw.ok && vPw.value === null) next.password = '비밀번호를 입력하거나 비밀번호 사용을 꺼주세요.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !vName.ok || !vMax.ok || !vNick.ok || !vPw.ok) return;

    setSubmitting(true);
    try {
      const result = await api.createRoom({
        name: vName.value,
        maxParticipants: vMax.value,
        nickname: vNick.value,
        ...(usePassword && vPw.value ? { password: vPw.value } : {}),
      });
      saveSession(result.roomId, { participantId: result.participantId, token: result.token, nickname: vNick.value });
      toast('방이 만들어졌어요! 동료들을 초대해보세요 🎉', 'success');
      navigate(`/room/${result.roomId}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiRequestError) {
        if (err.field && ['name', 'maxParticipants', 'nickname', 'password'].includes(err.field)) {
          setErrors({ [err.field]: err.message });
        } else {
          setErrors({ form: err.message });
        }
      } else {
        setErrors({ form: '방을 만들지 못했어요. 잠시 후 다시 시도해주세요.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-up">
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-extrabold">방 만들기</h1>
        <p className="mt-1 text-sm text-charcoal-500">회원가입 없이 바로 시작해요</p>
      </div>

      <form onSubmit={handleSubmit} className="card flex flex-col gap-5 p-5 sm:p-6" noValidate>
        <TextField
          id="room-name"
          label="방 이름 (선택)"
          placeholder={LIMITS.DEFAULT_ROOM_NAME}
          value={name}
          maxLength={LIMITS.ROOM_NAME_MAX}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
          hint={`최대 ${LIMITS.ROOM_NAME_MAX}자 · 비워두면 "${LIMITS.DEFAULT_ROOM_NAME}"`}
          autoComplete="off"
        />

        <div>
          <span className="label">참여 인원 (방장 포함)</span>
          <Stepper value={maxParticipants} min={LIMITS.PARTICIPANTS_MIN} max={LIMITS.PARTICIPANTS_MAX} onChange={setMaxParticipants} label="참여 인원" />
          {errors.maxParticipants ? (
            <p className="mt-1.5 text-sm text-red-600">{errors.maxParticipants}</p>
          ) : (
            <p className="mt-1.5 text-xs text-charcoal-500">
              {maxParticipants === 1 ? '혼자서도 랜덤으로 정할 수 있어요.' : `${maxParticipants}명이 모두 입장하면 자동으로 시작돼요.`}
              {maxParticipants > 4 && ' 한 게임에서는 최대 4명이 직접 선택해요.'}
            </p>
          )}
        </div>

        <TextField
          id="host-nickname"
          label="내 닉네임"
          placeholder="예: 김대리"
          value={nickname}
          maxLength={LIMITS.NICKNAME_MAX}
          onChange={(e) => setNickname(e.target.value)}
          error={errors.nickname}
          hint={`${LIMITS.NICKNAME_MIN}~${LIMITS.NICKNAME_MAX}자 · 한글, 영문, 숫자`}
          autoComplete="nickname"
          required
        />

        <div className="rounded-2xl border border-cream-300 bg-cream-50 p-4">
          <label className="flex cursor-pointer items-center justify-between gap-3">
            <span className="flex items-center gap-2 font-semibold">
              <Lock size={16} className="text-charcoal-500" aria-hidden="true" />
              비밀번호 설정
            </span>
            <span className="relative inline-flex h-7 w-12 items-center">
              <input type="checkbox" className="peer sr-only" checked={usePassword} onChange={(e) => setUsePassword(e.target.checked)} />
              <span className="absolute inset-0 rounded-full bg-cream-300 transition-colors peer-checked:bg-primary-500 peer-focus-visible:ring-4 peer-focus-visible:ring-primary-200" />
              <span className="absolute left-1 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
            </span>
          </label>
          {usePassword ? (
            <div className="mt-3">
              <TextField
                id="room-password"
                label="방 비밀번호"
                type="password"
                placeholder={`${LIMITS.PASSWORD_MIN}~${LIMITS.PASSWORD_MAX}자`}
                value={password}
                maxLength={LIMITS.PASSWORD_MAX}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                autoComplete="new-password"
              />
            </div>
          ) : (
            <p className="mt-2 text-xs text-charcoal-500">비밀번호 사용 안 함 · 링크만 있으면 누구나 입장할 수 있어요.</p>
          )}
        </div>

        {errors.form && (
          <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            {errors.form}
          </p>
        )}

        <button type="submit" className="btn-primary btn-lg w-full" disabled={submitting}>
          <Sparkles size={20} aria-hidden="true" />
          {submitting ? '방을 만드는 중...' : '방 만들기'}
        </button>
      </form>
    </div>
  );
}
